import { useTranslation } from 'react-i18next';
import {
  CaretDown,
  CaretRight,
  DotsSixVertical,
  Pencil,
  Trash,
} from '@phosphor-icons/react';
import { COLORS } from 'assets/styled';
import Icon from 'components/Icon/Icon';
import ToggleSwitch from 'components/ToggleSwitch/ToggleSwitch';
import Tooltip from 'components/Tooltip/Tooltip';
import { Level } from 'components/Modals/Modal/type';
import { IConfigurationQuestion } from 'types/ConfigurationQuestion.types';
import { OptionsManager } from '../OptionsManager';
import {
  DragHandle,
  IconButton,
  OptionsCount,
  OptionsPanel,
  Row,
  RowActions,
  RowLabel,
  RowMain,
  RowSubInfo,
  SlugBadge,
  ToggleCell,
  ToggleLabel,
} from './QuestionRow.styled';

interface IQuestionRowProps {
  question: IConfigurationQuestion;
  configurationId: number;
  currency: string;
  level: Level;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onToggleRequired: () => void;
  onToggleActive: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

export const QuestionRow = ({
  question,
  configurationId,
  currency,
  level,
  isExpanded,
  onToggleExpand,
  onToggleRequired,
  onToggleActive,
  onEdit,
  onDelete,
}: IQuestionRowProps) => {
  const { t } = useTranslation();
  const optionsCount = question.options_count ?? question.options?.length ?? 0;
  const hasOptions = optionsCount > 0;

  return (
    <>
      <Row>
        <DragHandle>
          <Icon
            svg={DotsSixVertical}
            size={20}
            color={COLORS.BORDER}
            weight="bold"
          />
        </DragHandle>
        <IconButton onClick={onToggleExpand}>
          <Icon
            svg={isExpanded ? CaretDown : CaretRight}
            size={18}
            color={COLORS.BLACK}
          />
        </IconButton>
        <RowMain>
          <RowLabel>{question.label}</RowLabel>
          <RowSubInfo>
            <SlugBadge>{question.slug}</SlugBadge>
            <OptionsCount>
              {optionsCount === 1
                ? t('1 option')
                : t('{{COUNT}} options').replace(
                    '{{COUNT}}',
                    String(optionsCount)
                  )}
            </OptionsCount>
          </RowSubInfo>
        </RowMain>
        <RowActions>
          <ToggleCell>
            <ToggleLabel>{t('Required')}</ToggleLabel>
            <ToggleSwitch
              isChecked={question.is_required}
              onClick={onToggleRequired}
            />
          </ToggleCell>
          <ToggleCell>
            <ToggleLabel>{t('Active')}</ToggleLabel>
            <Tooltip
              content={
                !hasOptions
                  ? t('Add at least one option before activating this question')
                  : undefined
              }
            >
              <ToggleSwitch
                isChecked={question.is_active}
                disabled={!hasOptions}
                onClick={onToggleActive}
              />
            </Tooltip>
          </ToggleCell>
          <IconButton onClick={onEdit}>
            <Icon svg={Pencil} size={20} color={COLORS.GREEN} />
          </IconButton>
          <IconButton onClick={onDelete}>
            <Icon svg={Trash} size={20} color={COLORS.RED_100} />
          </IconButton>
        </RowActions>
      </Row>
      {isExpanded && (
        <OptionsPanel>
          <OptionsManager
            question={question}
            configurationId={configurationId}
            currency={currency}
            level={level}
          />
        </OptionsPanel>
      )}
    </>
  );
};
